import { useMemo } from 'react';
import styled from 'styled-components';

import { Label1, Value4 } from '../Text';
import { Row } from './components';

const TableContent = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 0.75rem;
`;

const Header = styled.thead`
  top: 0;
  position: sticky;
  position: -webkit-sticky;
  background: ${({ theme }) => theme.bg2};
  box-shadow: 0rem 0.066rem ${({ theme }) => theme.bg5};
`;

const HeaderCell = styled(Label1)`
  text-align: left;
  color: ${({ theme }) => theme.gray4};
  margin-right: auto;
  padding: 0.625rem 0.75rem;
`;

const Cell = styled(Value4)`
  text-align: left;
  margin-right: auto;
  padding: 0.5rem 0.75rem;
`;

const Body = styled.tbody`
  overflow: scroll;
`;

interface TableProps<T> {
  data: T[] | null;
  headerData: T;
  onRowClick?: (row: T) => void;
  className?: string;
}

export function Table<T extends { id: string }>({
  data,
  headerData,
  onRowClick,
  className,
}: TableProps<T>) {
  const renderHeader = useMemo(() => {
    return (
      <tr>
        {Object.entries(headerData).map((e) =>
          e[0] === 'id' ? null : (
            <HeaderCell as="th" key={e[0]}>
              {e[1]}
            </HeaderCell>
          ),
        )}
      </tr>
    );
  }, [headerData]);

  const renderTableData = useMemo(
    () =>
      data?.map((row) => (
        <Row
          key={row.id}
          hoverable={!!onRowClick}
          onClick={() => {
            onRowClick?.(row);
          }}
        >
          {Object.entries(row).map((e) =>
            e[0] === 'id' ? null : (
              <Cell as="td" key={e[0]}>
                {e[1]}
              </Cell>
            ),
          )}
        </Row>
      )),
    [data, onRowClick],
  );

  if (headerData && data) {
    return (
      <TableContent className={className}>
        <Header>{renderHeader}</Header>
        <Body>{renderTableData}</Body>
      </TableContent>
    );
  }
  return null;
}

Table.defaultProps = {
  className: '',
  onRowClick: undefined,
};
